/**
 * The hero fallback — what the player shows when the creator is offline.
 *
 * Three platforms' back catalogues merged into one list and sorted by recency,
 * so an offline page still opens on the creator's most recent thing rather than
 * on an empty player.
 *
 * Split out of `StreamPanel.tsx`: it owns three queries and a forty-line merge
 * that nothing else in that component touches, and the file was past the
 * 400-line ceiling.
 */
import { useMemo } from "react";
import { useGetDropTwitchVods } from "@/hooks/drop/useGetDropTwitchVods";
import { useGetDropKickVods } from "@/hooks/drop/useGetDropKickVods";
import { useGetDropYouTubeVideos } from "@/hooks/drop/useGetDropYouTubeVideos";

/** One past broadcast, normalised across the three platforms' shapes. */
export interface HeroFallback {
    id: string;
    title: string;
    url: string;
    thumbnailUrl: string;
    when: string;
    durationSeconds: number;
    viewCount: number;
    source: "TWITCH" | "KICK" | "YOUTUBE";
    hlsUrl?: string;
}

export interface HeroFallbackResult {
    hero: HeroFallback | null;
    /** True while any of the three catalogues is still loading. */
    isLoading: boolean;
}

export function useHeroFallback(dropSlug?: string): HeroFallbackResult {
    const vodsQuery = useGetDropTwitchVods(dropSlug);
    const kickVodsQuery = useGetDropKickVods(dropSlug);
    const youtubeQuery = useGetDropYouTubeVideos(dropSlug);

    const hero = useMemo<HeroFallback | null>(() => {
        const twitchVods = (vodsQuery.data as { vods?: Array<{
            id: string; title: string; url: string; thumbnailUrl: string;
            createdAt: string; durationSeconds: number; viewCount: number;
        }> } | null | undefined)?.vods ?? [];
        const kickVods = (kickVodsQuery.data as { vods?: Array<{
            id: string; title: string; url: string; thumbnailUrl: string;
            createdAt: string; durationSeconds: number; viewCount: number;
            hlsUrl?: string;
        }> } | null | undefined)?.vods ?? [];
        const youtubeVids = (youtubeQuery.data as { videos?: Array<{
            videoId: string; title: string; thumbnailUrl: string;
            publishedAt: string; durationSeconds?: number; viewCount?: number;
        }> } | null | undefined)?.videos ?? [];

        const all: HeroFallback[] = [
            ...twitchVods.map(v => ({
                id: v.id, title: v.title, url: v.url, thumbnailUrl: v.thumbnailUrl,
                when: v.createdAt, durationSeconds: v.durationSeconds, viewCount: v.viewCount,
                source: "TWITCH" as const,
            })),
            ...kickVods.map(v => ({
                id: v.id, title: v.title, url: v.url, thumbnailUrl: v.thumbnailUrl,
                when: v.createdAt, durationSeconds: v.durationSeconds, viewCount: v.viewCount,
                source: "KICK" as const, hlsUrl: v.hlsUrl,
            })),
            ...youtubeVids.map(v => ({
                id: v.videoId, title: v.title,
                url: `https://www.youtube.com/watch?v=${v.videoId}`,
                thumbnailUrl: v.thumbnailUrl, when: v.publishedAt,
                durationSeconds: v.durationSeconds ?? 0, viewCount: v.viewCount ?? 0,
                source: "YOUTUBE" as const,
            })),
        ];
        /*
         * Drop unparseable `when` — NaN dates produce non-deterministic sort
         * order (comparator returns NaN). A malformed upstream record could
         * otherwise nondeterministically become the hero. Real VODs always
         * have a valid date.
         */
        const valid = all.filter(item => !Number.isNaN(new Date(item.when).getTime()));
        valid.sort((a, b) => new Date(b.when).getTime() - new Date(a.when).getTime());
        return valid[0] ?? null;
    }, [vodsQuery.data, kickVodsQuery.data, youtubeQuery.data]);

    return {
        hero,
        isLoading: vodsQuery.isLoading || kickVodsQuery.isLoading || youtubeQuery.isLoading,
    };
}
